import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  PageHeader,
  Skeleton,
  Table,
  TableShell,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from '@provenance/design';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import { auDateTime } from '../lib/format';
import { useSession } from '../lib/session';

interface Admin {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  active: boolean;
  hasPassword: boolean;
  lastLoginAt: string | null;
}

/**
 * Platform administrators. Each one is invited and sets their own password; the platform
 * always keeps at least one active, and nobody can deactivate themselves.
 */
export function PlatformAdmins() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useSession();
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Admin | null>(null);

  const admins = useQuery({ queryKey: ['platform-admins'], queryFn: () => api.get<Admin[]>('/api/console/platform/admins') });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['platform-admins'] });
  const fail = (title: string) => (error: unknown) =>
    toast.error(title, error instanceof ApiError ? error.message : 'Something went wrong.');

  const setActive = useMutation({
    mutationFn: (input: { id: string; active: boolean }) => api.post(`/api/console/platform/admins/${input.id}/active`, { active: input.active }),
    onSuccess: (_r, input) => {
      toast.success(input.active ? 'Reactivated' : 'Deactivated', input.active ? undefined : 'They are signed out everywhere.');
      refresh();
    },
    onError: fail('Could not change the account'),
  });
  const resend = useMutation({
    mutationFn: (id: string) => api.post(`/api/console/platform/admins/${id}/resend-invitation`),
    onSuccess: () => toast.success('Invitation sent again', 'The previous link no longer works.'),
    onError: fail('Could not resend the invitation'),
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Administrators"
        lede="Platform administrators create chambers, manage integrators and see platform-wide settings. Each one is invited and chooses their own password."
        actions={
          <Button variant="primary" leadingIcon={<UserPlus className="size-3.5" />} onClick={() => setInviting(true)}>
            Invite an administrator
          </Button>
        }
      />

      {admins.isLoading ? (
        <Skeleton className="h-48" />
      ) : (
        <TableShell>
          <Table>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Sign-in email</Th>
                <Th>Status</Th>
                <Th>Last signed in</Th>
                <Th>
                  <span className="sr-only">Actions</span>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {(admins.data ?? []).map((a) => {
                const isYou = a.id === user?.id;
                return (
                  <Tr key={a.id}>
                    <Td>
                      <span className="font-medium">
                        {a.firstName} {a.lastName}
                      </span>
                      {isYou && <span className="ml-1.5 [font-size:12px] text-[var(--pv-faint)]">(you)</span>}
                    </Td>
                    <Td>{a.email}</Td>
                    <Td>
                      {!a.active ? (
                        <Badge tone="neutral" size="sm">Deactivated</Badge>
                      ) : a.hasPassword ? (
                        <Badge tone="ok" size="sm" dot>Active</Badge>
                      ) : (
                        <Badge tone="warn" size="sm" dot>Invited</Badge>
                      )}
                    </Td>
                    <Td>
                      <span className="pv-num [font-size:12.5px] text-[var(--pv-muted)]">{a.lastLoginAt ? auDateTime(a.lastLoginAt) : '—'}</span>
                    </Td>
                    <Td>
                      <span className="flex flex-wrap justify-end gap-1.5">
                        <Button size="sm" variant="ghost" leadingIcon={<Pencil className="size-3.5" />} onClick={() => setEditing(a)} aria-label={`Edit ${a.email}`}>
                          Edit
                        </Button>
                        {!a.hasPassword && a.active && (
                          <Button size="sm" variant="ghost" loading={resend.isPending} onClick={() => resend.mutate(a.id)}>
                            Resend invite
                          </Button>
                        )}
                        {!isYou && (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={setActive.isPending}
                            onClick={() => setActive.mutate({ id: a.id, active: !a.active })}
                          >
                            {a.active ? 'Deactivate' : 'Reactivate'}
                          </Button>
                        )}
                      </span>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableShell>
      )}

      <Card>
        <CardHeader
          title="Good practice"
          description="Keep at least two active administrators, so losing one person's access never locks the platform. Use a personal work address for each — not a shared mailbox."
        />
      </Card>

      {inviting && <AdminDialog onClose={() => setInviting(false)} onDone={refresh} />}
      {editing && <AdminDialog admin={editing} isYou={editing.id === user?.id} onClose={() => setEditing(null)} onDone={refresh} />}
    </div>
  );
}

/** Invite (no `admin`) or edit (with one). */
function AdminDialog({ admin, isYou, onClose, onDone }: { admin?: Admin; isYou?: boolean; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [firstName, setFirstName] = useState(admin?.firstName ?? '');
  const [lastName, setLastName] = useState(admin?.lastName ?? '');
  const [email, setEmail] = useState(admin?.email ?? '');
  const emailChanged = Boolean(admin && email.trim().toLowerCase() !== admin.email);

  const save = useMutation({
    mutationFn: () =>
      admin
        ? api.patch(`/api/console/platform/admins/${admin.id}`, { firstName, lastName, email: email.trim() })
        : api.post('/api/console/platform/admins', { firstName, lastName, email: email.trim() }),
    onSuccess: () => {
      toast.success(
        admin ? 'Saved' : 'Invitation sent',
        admin
          ? emailChanged
            ? `They now sign in with ${email.trim().toLowerCase()}.`
            : undefined
          : `${email.trim()} will receive a link to choose a password.`,
      );
      onDone();
      onClose();
    },
    onError: (error) => toast.error('Could not save', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={admin ? `Edit ${admin.firstName} ${admin.lastName}` : 'Invite a platform administrator'}
          description={
            admin
              ? 'Changing the email changes the address used to sign in. The password stays the same.'
              : 'They receive an email with a single-use link to choose their own password. Nobody else ever sees it.'
          }
        />
        <DialogBody>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="First name" required>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="off" />
            </Field>
            <Field label="Last name" required>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="off" />
            </Field>
            <Field
              label="Sign-in email"
              required
              className="sm:col-span-2"
              help={emailChanged && isYou ? 'Use this address next time you sign in.' : undefined}
            >
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!firstName.trim() || !lastName.trim() || !email.trim()}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {admin ? 'Save' : 'Send invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
